/**
 * StreetScene — speech bubbles over a street scene (a greeting, a grievance
 * aired, news passed on, a territorial warning): follows the talkers' live
 * bodies, fades out over the last seconds. No props — the scene IS the two
 * versonas, which the server already poses face to face ('chatting').
 *
 * EAVESDROP (2026-09-10): a detail carrying EXCHANGE_SEP is a two-line
 * exchange — the first line shows over the host for the first part of the
 * scene, then the reply over the participant. A plain detail is one bubble
 * over the host, as before.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { EXCHANGE_SEP } from '@shared/living_events.js';
import { textTexture } from '../fx_textures.js';
import { type Stage, type StageCtx, disposeGroup } from './StageBase.js';

/** The share of the scene the first line holds before the reply takes over. */
export const EXCHANGE_FIRST_SHARE = 0.55;

interface Bubble { sprite: THREE.Sprite; over: string }

export class StreetScene implements Stage {
  readonly group = new THREE.Group();
  private bubbles: Bubble[] = [];
  private total: number;

  constructor(private readonly ctx: StageCtx) {
    this.group.position.set(ctx.x, ctx.y, ctx.z);
    ctx.root.add(this.group);
    this.total = Math.max(1, ctx.remainingS());
    const raw = ctx.detail.split(' — at ')[0];
    const parts = raw.split(EXCHANGE_SEP).map((t) => t.replace(/^"|"$/g, '').trim()).filter((t) => t.length > 0);
    const first = parts[0] ?? '…';
    this.bubbles.push({ sprite: makeBubble(first), over: ctx.hostId });
    if (parts.length > 1 && ctx.participantIds.length > 0) {
      const reply = makeBubble(parts[1]);
      reply.visible = false;
      this.bubbles.push({ sprite: reply, over: ctx.participantIds[0] });
    }
    for (const b of this.bubbles) this.group.add(b.sprite);
  }

  /** Which bubble is up: the first line, then (an exchange) the reply. */
  private activeIndex(): number {
    if (this.bubbles.length < 2) return 0;
    const elapsed = this.total - this.ctx.remainingS();
    return elapsed < this.total * EXCHANGE_FIRST_SHARE ? 0 : 1;
  }

  update(t: number): void {
    const idx = this.activeIndex();
    const left = this.ctx.remainingS();
    this.bubbles.forEach((b, i) => {
      const on = i === idx;
      b.sprite.visible = on;
      if (!on) return;
      // Follow the speaker; fall back to the other talker, then the scene point.
      const p = this.ctx.versonaPosition(b.over) ?? (i === 1 ? this.ctx.versonaPosition(this.ctx.hostId) : null);
      if (p) this.group.position.set(p.x, p.y, p.z);
      b.sprite.position.y = 2.5 + Math.sin(t * 1.5) * 0.05;
      const fade = left < 6 ? Math.max(0, left / 6) : 1;
      (b.sprite.material as THREE.SpriteMaterial).opacity = fade;
    });
  }

  describe(): Record<string, number | string | boolean> {
    return { family: 'street', following: !!this.ctx.versonaPosition(this.ctx.hostId), lines: this.bubbles.length, active: this.activeIndex() };
  }

  dispose(): void { disposeGroup(this.group); }
}

/** A speech-bubble sprite for one line of talk (dark ink on a light plate). */
function makeBubble(text: string): THREE.Sprite {
  const lines = wrap(text, 30).slice(0, 3);
  const tex = textTexture(lines.map((t, i) => ({ text: t, font: i === 0 && lines.length === 1 ? '600 30px system-ui' : '26px system-ui' })), 512, 60 + lines.length * 44, 'rgba(244,240,255,0.92)', 'rgba(60,40,90,0.5)');
  // Dark text on the light bubble: redraw the lines in ink.
  const cv = tex.image as HTMLCanvasElement;
  const g = cv.getContext('2d')!;
  g.textAlign = 'center'; g.textBaseline = 'middle';
  const step = cv.height / (lines.length + 1);
  lines.forEach((t, i) => { g.font = '600 28px system-ui'; g.fillStyle = '#1c1430'; g.fillText(t, cv.width / 2, step * (i + 1)); });
  tex.needsUpdate = true;
  const sprite = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false })));
  (sprite.material as THREE.SpriteMaterial & { ownsMap?: boolean }).ownsMap = true;
  sprite.scale.set(4.2, (cv.height / 512) * 4.2, 1);
  sprite.position.set(0, 2.5, 0);
  return sprite;
}

export function wrap(text: string, width: number): string[] {
  const words = text.split(/\s+/);
  const out: string[] = [];
  let line = '';
  for (const w of words) {
    if ((line + ' ' + w).trim().length > width && line) { out.push(line.trim()); line = w; }
    else line = `${line} ${w}`;
  }
  if (line.trim()) out.push(line.trim());
  return out.length ? out : ['…'];
}
